import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, MapPin, Wallet, AlertCircle, ChevronRight, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useCart } from '../context/CartContext';

const COIN_COLORS = { BTC:'#F7931A', ETH:'#627EEA', BNB:'#F0B90B', SOL:'#9945FF', USDT:'#26A17B', SUI:'#4DA2FF', ASTER:'#FF6B00', HYPE:'#E040FB', XRP:'#0052FF' };

export default function Checkout() {
  const navigate = useNavigate();
  const { sellerGroups, clearCart, cartTotalUSD } = useCart();
  const [addr, setAddr] = useState({ fullName:'', phone:'', street:'', city:'', state:'', country:'', postalCode:'' });
  const [balances, setBalances]   = useState({});
  const [prices, setPrices]       = useState({});
  const [loading, setLoading]     = useState(false);
  const [quoting, setQuoting]     = useState(false);
  const [quotes, setQuotes]       = useState({});   // { sellerId: { coin, cryptoAmount, usdtWillLock } }
  const [ordersPlaced, setOrdersPlaced] = useState([]);
  const [step, setStep]           = useState('address');

  useEffect(() => {
    if (!sellerGroups?.length && !ordersPlaced.length) navigate('/cart');
    api.get('/wallet/balance').then(r => setBalances(r.data.balance || {})).catch(() => {});
    api.get('/prices/live').then(r => setPrices(r.data.prices || {})).catch(() => {});
  }, []);

  // Get swap quote for each group so buyer sees "X SOL → Y USDT" before confirming
  const getQuotes = async () => {
    if (!addr.fullName || !addr.city || !addr.country) return toast.error('Fill in your shipping address first');
    setQuoting(true);
    const q = {};
    for (const group of sellerGroups) {
      const coin = group.items[0]?.listedCurrency || 'BNB';
      const groupUSD = group.items.reduce((s,i) => s + (i.priceUSD * 1.015 * i.quantity), 0);
      const coinPrice = prices[coin] || 1;
      const cryptoAmount = groupUSD / coinPrice;
      const available = balances[coin] || 0;
      q[group.sellerId] = {
        coin, cryptoAmount, usdtWillLock: groupUSD,
        available, sufficient: available >= cryptoAmount,
        shortfall: Math.max(0, cryptoAmount - available),
      };
    }
    setQuotes(q);
    setStep('confirm');
    setQuoting(false);
  };

  const placeOrders = async () => {
    setLoading(true);
    const results = [];
    try {
      for (const group of sellerGroups) {
        const coin = group.items[0]?.listedCurrency || 'BNB';
        const res = await api.post('/orders/checkout', {
          items: group.items.map(i => ({ productId:i.productId, quantity:i.quantity, selectedVariant:i.selectedVariant||null })),
          shippingAddress: addr,
          paymentCurrency: coin,
        });
        results.push({ order: res.data.order, coin, sellerUsername: group.sellerUsername, escrowUSDT: res.data.escrowUSDT });
      }
      clearCart();
      setOrdersPlaced(results);
      setStep('done');
      toast.success('Orders placed!');
    } catch (err) {
      const msg = err.response?.data?.message || 'Checkout failed';
      toast.error(msg, { duration: 6000 });
    } finally { setLoading(false); }
  };

  // ── ADDRESS STEP ─────────────────────────────────────────────
  if (step === 'address') return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mt-4 mb-1" style={{ fontFamily:'Sora' }}>Checkout</h1>
      <p className="text-brand-muted text-sm mb-5">Funds leave your wallet and lock in escrow instantly</p>

      {/* Wallet preview */}
      <div className="glass rounded-2xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Wallet size={15} className="text-brand-orange" />
          <span className="font-bold text-sm">Your Wallet</span>
          <Link to="/wallet" className="ml-auto text-xs text-brand-orange hover:underline">Top Up →</Link>
        </div>
        {Object.keys(balances).length === 0 ? (
          <div className="flex items-center gap-2 p-2 rounded-lg text-xs" style={{ background:'rgba(255,50,50,0.08)', border:'1px solid rgba(255,50,50,0.2)' }}>
            <AlertCircle size={12} className="text-red-400" />
            <span className="text-red-300">No balance. Please deposit first.</span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {Object.entries(balances).filter(([,v]) => v > 0).map(([coin, bal]) => (
              <div key={coin} className="px-2.5 py-1 rounded-lg text-xs font-mono" style={{ background:'#0A0A0F', border:`1px solid ${COIN_COLORS[coin]||'#1E1E2E'}40` }}>
                <span className="font-bold" style={{ color:COIN_COLORS[coin] }}>{coin}</span>
                <span className="text-brand-muted ml-1">{bal.toFixed(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order summary */}
      <div className="glass rounded-2xl p-4 mb-4">
        <h2 className="font-bold text-sm mb-3">Order Summary</h2>
        {sellerGroups.map((g, i) => {
          const coin = g.items[0]?.listedCurrency || 'BNB';
          const total = g.items.reduce((s,item) => s + (item.priceUSD*1.015*item.quantity), 0);
          return (
            <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: i<sellerGroups.length-1?'1px solid #1E1E2E':'none' }}>
              <div>
                <p className="text-sm font-semibold">🏪 {g.sellerUsername||'Seller '+(i+1)}</p>
                <p className="text-xs text-brand-muted">{g.items.length} item{g.items.length!==1?'s':''} · Pay in {coin}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-brand-muted">${total.toFixed(2)}</p>
                <p className="text-xs font-mono mt-0.5" style={{ color:COIN_COLORS[coin] }}>≈ {(total/(prices[coin]||1)).toFixed(6)} {coin}</p>
              </div>
            </div>
          );
        })}
        <div className="flex justify-between font-bold mt-3 pt-3 text-sm" style={{ borderTop:'1px solid #1E1E2E' }}>
          <span>Grand Total</span>
          <span className="gradient-text">${cartTotalUSD?.toFixed(2)} USD</span>
        </div>
      </div>

      {/* Shipping address */}
      <div className="glass rounded-2xl p-4 mb-5">
        <h2 className="font-bold mb-3 text-sm flex items-center gap-2"><MapPin size={14} className="text-brand-orange" /> Shipping Address</h2>
        <div className="grid grid-cols-2 gap-2">
          {[['fullName','Full Name','col-span-2'],['phone','Phone',''],['street','Street Address','col-span-2'],['city','City',''],['state','State / Province',''],['country','Country',''],['postalCode','Postal Code','']].map(([key,ph,cls]) => (
            <input key={key} className={`input-dark text-sm ${cls}`} placeholder={ph}
              value={addr[key]} onChange={e => setAddr({...addr,[key]:e.target.value})} />
          ))}
        </div>
      </div>

      <button onClick={getQuotes} disabled={quoting} className="btn-primary w-full py-3 flex items-center justify-center gap-2 text-base">
        {quoting ? <div className="spinner" /> : <><Shield size={16} /> Review Order & Swap Preview</>}
      </button>
    </div>
  );

  // ── CONFIRM STEP ─────────────────────────────────────────────
  if (step === 'confirm') return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mt-4 mb-2" style={{ fontFamily:'Sora' }}>Confirm & Pay</h1>
      <p className="text-brand-muted text-sm mb-5">Your coin will swap to USDT the moment you pay — price locked instantly</p>

      <div className="space-y-3 mb-5">
        {sellerGroups.map((g, i) => {
          const q = quotes[g.sellerId] || {};
          const color = COIN_COLORS[q.coin] || '#FF6B00';
          return (
            <div key={i} className="rounded-2xl p-4" style={{ background:'#12121A', border:`1px solid ${q.sufficient?color+'40':'rgba(255,50,50,0.4)'}` }}>
              <p className="font-bold text-sm mb-3">🏪 {g.sellerUsername||'Seller '+(i+1)}</p>

              {/* Swap preview arrow box */}
              <div className="flex items-center gap-3 p-3 rounded-xl mb-3" style={{ background:'#0A0A0F' }}>
                <div className="text-center flex-1">
                  <p className="text-xs text-brand-muted mb-1">You pay</p>
                  <p className="font-bold font-mono text-sm" style={{ color }}>{q.cryptoAmount?.toFixed(6)} {q.coin}</p>
                  <p className="text-xs text-brand-muted">from your wallet</p>
                </div>
                <div className="text-xl">→</div>
                <div className="text-center flex-1">
                  <p className="text-xs text-brand-muted mb-1">Escrow locks</p>
                  <p className="font-bold font-mono text-sm" style={{ color:'#26A17B' }}>{q.usdtWillLock?.toFixed(2)} USDT</p>
                  <p className="text-xs text-brand-muted">swapped instantly</p>
                </div>
              </div>

              {/* Balance check */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-brand-muted">Your {q.coin} balance:</span>
                <span style={{ color: q.sufficient?'#00C864':'#FF3232' }}>
                  {q.available?.toFixed(6)} {q.coin} {q.sufficient?'✅':'❌'}
                </span>
              </div>

              {!q.sufficient && (
                <div className="mt-2 p-2 rounded-lg text-xs flex items-center gap-2" style={{ background:'rgba(255,50,50,0.08)', border:'1px solid rgba(255,50,50,0.2)' }}>
                  <AlertCircle size={12} className="text-red-400" />
                  <span className="text-red-300">Need {q.shortfall?.toFixed(6)} more {q.coin} — <Link to="/wallet" className="underline">deposit now</Link></span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Stability notice */}
      <div className="p-3 rounded-xl mb-5 text-xs flex items-start gap-2" style={{ background:'rgba(38,161,123,0.08)', border:'1px solid rgba(38,161,123,0.25)' }}>
        <Shield size={12} className="text-green-400 flex-shrink-0 mt-0.5" />
        <span className="text-green-200">Your coin swaps to USDT the instant you pay. Price is frozen — no matter what the market does, your order value stays the same until delivery.</span>
      </div>

      <div className="flex gap-3">
        <button onClick={() => setStep('address')} className="btn-outline flex-1 py-3 text-sm">← Back</button>
        <button onClick={placeOrders} disabled={loading || Object.values(quotes).some(q => !q.sufficient)}
          className="btn-primary flex-1 py-3 flex items-center justify-center gap-2 text-sm disabled:opacity-40">
          {loading ? <div className="spinner" /> : <><Shield size={14} /> Pay & Lock in Escrow</>}
        </button>
      </div>
    </div>
  );

  // ── DONE STEP ────────────────────────────────────────────────
  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-2xl mx-auto">
      <div className="mt-8 glass rounded-2xl p-6 text-center">
        <div className="text-5xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily:'Sora' }}>Orders Placed!</h2>
        <p className="text-brand-muted text-sm mb-2">Your funds have been swapped to USDT and locked in escrow.</p>
        <p className="text-xs text-green-400 mb-6">Price is now stable — sellers will receive the exact USDT amount regardless of market moves.</p>
        {ordersPlaced.map((r, i) => (
          <div key={i} className="p-3 rounded-xl mb-2 text-left" style={{ background:'#0A0A0F', border:'1px solid #1E1E2E' }}>
            <p className="font-mono text-xs text-brand-muted">#{r.order?.orderNumber}</p>
            <div className="flex justify-between text-sm mt-1">
              <span className="font-semibold">{r.sellerUsername}</span>
              <span style={{ color:'#26A17B' }}>{r.escrowUSDT?.toFixed(2)} USDT locked</span>
            </div>
          </div>
        ))}
        <Link to="/orders" className="btn-primary inline-flex items-center gap-2 mt-4 px-8">
          Track Orders <ChevronRight size={16} />
        </Link>
      </div>
    </div>
  );
}
